import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  Avatar,
  TextField,
  IconButton,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material'
import {
  Edit as EditIcon,
  Comment as CommentIcon,
  Attachment as AttachmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Person2 as Person2Icon,
  Alarm as AlarmIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  PriorityHigh as PriorityIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material'
import { addComment, fetchIssueAttachment } from '../../services/issueService'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useSelector } from 'react-redux'
import { selectUserInfo } from '../../../../redux/selectors/authSelector'

dayjs.extend(relativeTime)

const IssueDetail = ({ issue, onEdit, onClose }) => {
  const [comments, setComments] = useState(issue.comments || [])
  const [newComment, setNewComment] = useState('')
  const [loadingComment, setLoadingComment] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [loadingAttachments, setLoadingAttachments] = useState(false)

  const userInfo = useSelector(selectUserInfo)  

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'error'
      case 'Medium':
        return 'warning'
      case 'Low':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'error'
      case 'In Progress':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const handleAddComment = async () => {
    if(!newComment.trim()) return

    setLoadingComment(true)

    try {
       const commentData = {
        text: newComment,
        author: userInfo?.username || 'Current User',
        //type: 'comment',
      }
      console.log("Sending commentData:", JSON.stringify(commentData));
      console.log("Comment", commentData)
      const addedComment = await addComment(issue.id, commentData)

      setComments((prev) => [...prev, addedComment])
      setNewComment('')

      console.log("Comment added.")
    } catch (error) {
      console.error('Error adding comment:', error.response.data)
    } finally {
      setLoadingComment(false)
    }

  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleAddComment()
    }
  }

  const formatDuration = (startDate, endDate = null) => {
    const end = endDate ? dayjs(endDate) : dayjs()
    const start = dayjs(startDate)
    return end.from(start, true)
  }

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'comment':
        return <CommentIcon />
      case 'status_change':
        return <ScheduleIcon />
      case 'assignment':
        return <PersonIcon />
      default:
        return <CommentIcon />
    }
  }

  useEffect(() => {
    const loadAttachments = async () => {
      if (!issue.id) return
      
      setLoadingAttachments(true)
      const data = await fetchIssueAttachment(issue.id)
      console.log("Attachment data:", data)
      setAttachments(data)
      setLoadingAttachments(false)
    }
    loadAttachments()
  },[issue?.id])

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Issue Header */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                {issue.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Issue ID: {issue.id}
              </Typography>
              <Box display="flex" gap={1} mt={1}>
                <Chip
                  label={issue.priority}
                  color={getPriorityColor(issue.priority)}
                  size="small"
                  icon={<PriorityIcon />}
                />
                <Chip
                  label={issue.status}
                  color={getStatusColor(issue.status)}
                  size="small"
                />
                {issue.category && (
                  <Chip
                    label={issue.category}
                    variant="outlined"
                    size="small"
                    icon={<CategoryIcon />}
                  />
                )}
                {issue.issueType && (
                  <Chip
                    label={issue.issueType}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edit Issue
            </Button>
          </Box>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Description */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
              {issue.description || 'No description provided'}
            </Typography>
          </Paper>

          {/* Root Cause & Action Taken */}
          {(issue.rootCausePreliminary || issue.actionTaken) && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Analysis & Actions
              </Typography>
              {issue.rootCausePreliminary && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Root Cause
                  </Typography>
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {issue.rootCausePreliminary}
                  </Typography>
                </Box>
              )}
              {issue.actionTaken && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Remarks
                  </Typography>
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {issue.actionTaken}
                  </Typography>
                </Box>
              )}
              {issue.nextActionFollowUpPlan && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Action Plan 
                  </Typography>
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {issue.nextActionFollowUpPlan}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Activity Timeline */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activity Timeline
            </Typography>
            <Box>
              {/* Issue Creation */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  bgcolor: 'grey.50',
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" color="textSecondary">
                    {dayjs(issue.createdAt).format('DD MMM YYYY, HH:mm')}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  Issue created by {issue.createdBy || 'System'}
                </Typography>
              </Box>

              {/* Comments and Updates */}
              {comments.map((comment, index) => (
                <Box
                  key={comment.id || index}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderLeft: 3,
                    borderColor: 'secondary.main',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    {getTimelineIcon(comment.type)}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ ml: 1 }}
                    >
                      {dayjs(comment.createdAt).format('DD MMM YYYY, HH:mm')}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    <strong>{comment.author}:</strong> {comment.text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Add Comment */}
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Add Comment
              </Typography>
              <Box display="flex" gap={1} alignItems="flex-end">
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add a comment... (Ctrl+Enter to send)"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                />
                <IconButton
                  color="primary"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || loadingComment}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar Information */}
        <Grid item xs={12} md={4}>
          {/* Issue Details */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Issue Details
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Plant" 
                  secondary={`${issue.plantName || 'N/A'}${issue.plantCapacity ? ` (${issue.plantCapacity} kWp)` : ''}`} 
                />
              </ListItem>
              {issue.reportedBy && (
                <ListItem>
                  <ListItemIcon>
                    <Person2Icon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Reported By"
                    secondary={issue.reportedBy}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Assigned To (Internal)"
                  secondary={issue.assignedTo || 'Unassigned'}
                />
              </ListItem>
              {issue.responsibleTeamExternal && (
                <ListItem>
                  <ListItemIcon>
                    <EngineeringIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Assigned To (External)"
                    secondary={issue.responsibleTeamExternal}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Created"
                  secondary={dayjs(issue.createdAt).format(
                    'DD MMM YYYY, HH:mm'
                  )}
                />
              </ListItem>
              {issue.firstObservedDate && (
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="First Observed"
                    secondary={dayjs(issue.firstObservedDate).format(
                      'DD MMM YYYY'
                    )}
                  />
                </ListItem>
              )}
              {issue.expectedClosingDate && (
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expected Closing"
                    secondary={dayjs(issue.expectedClosingDate).format(
                      'DD MMM YYYY'
                    )}
                  />
                </ListItem>
              )}
              {issue.actualClosingDate && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Actual Closing"
                    secondary={dayjs(issue.actualClosingDate).format(
                      'DD MMM YYYY'
                    )}
                  />
                </ListItem>
              )}
              {issue.affectedEquipment && (
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Affected Equipment"
                    secondary={issue.affectedEquipment}
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Severity & Escalation */}
          {(issue.severityImpact || issue.escalationLevel || issue.delayDays !== null) && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Severity & Escalation
              </Typography>
              <List dense>
                {issue.severityImpact && (
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Severity Impact"
                      secondary={issue.severityImpact}
                    />
                  </ListItem>
                )}
                {issue.escalationLevel && (
                  <ListItem>
                    <ListItemIcon>
                      <PriorityIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Escalation Level"
                      secondary={issue.escalationLevel}
                    />
                  </ListItem>
                )}
                {issue.delayDays !== null && issue.delayDays !== undefined && (
                  <ListItem>
                    <ListItemIcon>
                      <AlarmIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Delay Days"
                      secondary={`${issue.delayDays} day${issue.delayDays !== 1 ? 's' : ''}`}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}

          {/* Customer Communication & Closure */}
          {(issue.customerCommunicationDone !== undefined || issue.proofOfClosure) && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Communication & Closure
              </Typography>
              <List dense>
                {issue.customerCommunicationDone !== undefined && (
                  <ListItem>
                    <ListItemIcon>
                      <CommentIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Customer Communication"
                      secondary={issue.customerCommunicationDone ? 'Completed' : 'Pending'}
                    />
                  </ListItem>
                )}
                {issue.proofOfClosure && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Proof of Closure"
                      secondary={issue.proofOfClosure}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}

          {/* Tags */}
          {issue.tags && issue.tags.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tags
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {issue.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Attachments */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Attachments
            </Typography>

            {loadingAttachments ? (
              <Typography variant="body2" color="textSecondary">
                Loading attachments...
              </Typography>
            ) : attachments.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No attachments uploaded for this issue.
              </Typography>
            ) : (
              <List dense>
                {attachments.map((att, index) => (
                  <ListItem
                    key={index}
                    component="a"
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    button
                  >
                    <ListItemIcon>
                      <AttachmentIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={att.originalName}
                      secondary={`${Math.round(att.size / 1024)} KB • ${dayjs(
                        att.createdAt
                      ).fromNow()}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* Time Tracking */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Time Tracking
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Time Since Created"
                  secondary={formatDuration(issue.createdAt)}
                />
              </ListItem>
              {issue.status === 'In Progress' && issue.startedAt && (
                <ListItem>
                  <ListItemText
                    primary="Time In Progress"
                    secondary={formatDuration(issue.startedAt)}
                  />
                </ListItem>
              )}
              {(issue.status === 'Resolved' || issue.status === 'Closed') &&
                issue.resolvedAt && (
                  <ListItem>
                    <ListItemText
                      primary="Resolution Time"
                      secondary={formatDuration(
                        issue.createdAt,
                        issue.resolvedAt
                      )}
                    />
                  </ListItem>
                )}
              <ListItem>
                <ListItemText
                  primary="Last Updated"
                  secondary={dayjs(issue.updatedAt).format('DD MMM YYYY, HH:mm')}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default IssueDetail